function get_realtime_payload(dom_event) {
  const payload = dom_event?.detail;
  if (!payload || typeof payload !== "object") return null;
  return payload;
}

function get_realtime_event_type(payload) {
  return payload?.event_type || payload?.type || null;
}

function normalize_id(value) {
  if (value === null || value === undefined) return null;
  return String(value);
}

function is_portal_management_payload(payload) {
  return payload?.module === "portal_management";
}

function is_same_user_action(payload, view_model) {
  const actor_user_id = normalize_id(payload?.data?.user_id);
  const current_user_id = normalize_id(view_model?.user_details?.user_id);
  return Boolean(actor_user_id && current_user_id && actor_user_id === current_user_id);
}

function patch_report_lists(view_model, patch_list) {
  view_model.reports = patch_list(view_model.reports);
  if (Array.isArray(view_model.reportsByTags)) view_model.reportsByTags = patch_list(view_model.reportsByTags);
  if (Array.isArray(view_model.reportsByCategories)) view_model.reportsByCategories = patch_list(view_model.reportsByCategories);
}

function find_report_in_list(list, report_id) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const idx = list.findIndex((r) => normalize_id(r?.id) === report_id);
  if (idx < 0) return null;
  return Reflect.get(list, idx);
}

function insert_comment_if_missing(report, newComment) {
  if (!report || !newComment) return false;
  const existing = Array.isArray(report?.comments) ? report.comments : [];

  const new_id = Number(newComment?.id);
  const already_present = new_id ? existing.some((c) => Number(c?.id) === new_id) : false;
  if (already_present) return false;

  report.comments = [newComment].concat(existing);
  report.total_comments = (report.total_comments || 0) + 1;
  return true;
}

function find_comment_in_report(report, report_comment_id) {
  const comments = Array.isArray(report?.comments) ? report.comments : [];
  if (comments.length === 0) return null;

  const idx = comments.findIndex((c) => normalize_id(c?.id) === report_comment_id);
  if (idx < 0) return null;
  return Reflect.get(comments, idx);
}

function insert_reply_if_missing(comment, reply) {
  if (!comment || !reply) return false;
  if (!Array.isArray(comment.reply_comment)) comment.reply_comment = [];

  const reply_id = normalize_id(reply?.id);
  const already_present = reply_id
    ? comment.reply_comment.some((existing) => normalize_id(existing?.id) === reply_id)
    : false;
  if (already_present) return false;

  comment.reply_comment = comment.reply_comment.concat([reply]);
  return true;
}

function patch_report_comment_reply_in_list(list, report_id, report_comment_id, reply) {
  const report = find_report_in_list(list, report_id);
  if (!report) return list;

  const comment = find_comment_in_report(report, report_comment_id);
  if (!comment) return list;

  const changed = insert_reply_if_missing(comment, reply);
  return changed ? Array.from(list) : list;
}

function patch_report_like_in_list(list, report_id, like_delta) {
  const report = find_report_in_list(list, report_id);
  if (!report) return list;

  const next_like_count = Number(report.like_count || 0) + Number(like_delta || 0);
  const next_total_likes = Number(report.total_likes || 0) + Number(like_delta || 0);

  report.like_count = Math.max(0, next_like_count);
  report.total_likes = Math.max(0, next_total_likes);

  return Array.from(list);
}

function apply_report_comment_added(view_model, payload) {
  const report_id = normalize_id(payload?.data?.report_id);
  const newComment = payload?.data?.comment;
  if (!report_id || !newComment) return;

  // Prevent the author from seeing a duplicate comment:
  // they already insert it locally on successful POST.
  if (is_same_user_action(payload, view_model)) return;

  const patch_list = (list) => {
    const report = find_report_in_list(list, report_id);
    if (!report) return list;

    const changed = insert_comment_if_missing(report, newComment);
    return changed ? Array.from(list) : list;
  };

  patch_report_lists(view_model, patch_list);
}

function apply_report_comment_reply_added(view_model, payload) {
  const report_id = normalize_id(payload?.data?.report_id);
  const report_comment_id = normalize_id(payload?.data?.report_comment_id);
  const reply = payload?.data?.reply;
  if (!report_id || !report_comment_id || !reply) return;

  // Prevent the author from seeing a duplicate reply:
  // the UI may already insert it locally on successful POST.
  if (is_same_user_action(payload, view_model)) return;

  const patch_list = (list) =>
    patch_report_comment_reply_in_list(list, report_id, report_comment_id, reply);

  patch_report_lists(view_model, patch_list);
}


function apply_report_like_delta(view_model, payload, like_delta) {
  const report_id = normalize_id(payload?.data?.report_id);
  if (!report_id) return;

  // Prevent the author from seeing duplicate updates:
  // the UI already patches likes locally on successful POST.
  if (is_same_user_action(payload, view_model)) return;

  const patch_list = (list) => patch_report_like_in_list(list, report_id, like_delta);
  patch_report_lists(view_model, patch_list);
}

function apply_report_like_added(view_model, payload) {
  apply_report_like_delta(view_model, payload, 1);
}

function apply_report_like_removed(view_model, payload) {
  apply_report_like_delta(view_model, payload, -1);
}

export function handle_report_realtime(view_model, dom_event) {
  const payload = get_realtime_payload(dom_event);
  if (!payload) return;
  if (!is_portal_management_payload(payload)) return;

  const event_type = get_realtime_event_type(payload);
  if (!event_type) return;

  if (event_type === "report_comment_added") {
    apply_report_comment_added(view_model, payload);
  }
  if (event_type === "report_comment_reply_added") {
    apply_report_comment_reply_added(view_model, payload);
  }
  if (event_type === "report_like_added") {
    apply_report_like_added(view_model, payload);
  }
  if (event_type === "report_like_removed") {
    apply_report_like_removed(view_model, payload);
  }
}
